import { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Title,
  Paper,
  Button,
  Group,
  Text,
  Card,
  Badge,
  Table,
  SimpleGrid,
  Skeleton,
  SegmentedControl,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { usePageTitle } from '../../hooks/usePageTitle';
import { SUBSCRIPTION_BADGES } from './adminUserShared';
import {
  AdminUserStats,
  ActivationFunnel,
  CohortHeatmap,
  ColumnChart,
  ActivationRateLines,
  SwapComparisonChart,
  RetentionTriangle,
} from './AdminCharts';

const formatPercent = (count: number, total: number) =>
  total > 0 ? `${Math.round((count / total) * 100)}%` : '–';

// How many signup weeks the cohort charts and retention triangle look back.
// The backend clamps to 4–52, so these just need to be sensible presets.
const WINDOW_OPTIONS = [
  { value: '8', label: '8w' },
  { value: '12', label: '12w' },
  { value: '26', label: '26w' },
  { value: '52', label: '52w' },
];
const DEFAULT_WINDOW = '12';

// One headline number with its share of the total.
const StatTile = ({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total?: number;
}) => (
  <Card withBorder radius="lg" padding="md">
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {label}
    </Text>
    <Group gap="xs" align="baseline">
      <Text size="xl" fw={700}>
        {value}
      </Text>
      {total !== undefined && (
        <Text size="sm" c="dimmed">
          {formatPercent(value, total)}
        </Text>
      )}
    </Group>
  </Card>
);

// Header for a chart card: title, the question the chart answers, refresh-free.
const ChartHeader = ({ title, answers }: { title: string; answers: string }) => (
  <div>
    <Title order={4}>{title}</Title>
    <Text size="sm" c="dimmed">
      {answers}
    </Text>
  </div>
);

export const AdminOverviewPage = () => {
  usePageTitle('Admin | Meals You Love');
  const { user } = useAppUser();
  const [stats, setStats] = useState<AdminUserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [windowWeeks, setWindowWeeks] = useState(DEFAULT_WINDOW);

  const loadStats = useCallback(async () => {
    if (!user) return;

    setStatsLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/stats?weeks=${windowWeeks}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        setStats(await response.json());
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to load activation stats',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load activation stats',
        color: 'red',
      });
    } finally {
      setStatsLoading(false);
    }
  }, [user, windowWeeks]);

  // Refetches when the window changes too (loadStats depends on it); charts
  // keep showing the previous window's data until the new response lands.
  useEffect(() => {
    if (!user?.isAdmin) return;
    loadStats();
  }, [user, loadStats]);

  const windowControls = (
    <Group gap="sm" wrap="nowrap">
      <SegmentedControl
        size="xs"
        value={windowWeeks}
        onChange={setWindowWeeks}
        data={WINDOW_OPTIONS}
        aria-label="Chart time window in weeks"
      />
      <Button
        leftSection={<IconRefresh size="1rem" />}
        variant="outline"
        onClick={loadStats}
        loading={statsLoading}
      >
        Refresh
      </Button>
    </Group>
  );

  if (!stats) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Paper withBorder p="md" radius="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Title order={3}>Activation & Retention</Title>
                  <Text size="sm" c="dimmed">
                    {statsLoading ? 'Loading stats...' : 'Stats unavailable.'}
                  </Text>
                </div>
                {windowControls}
              </Group>
              {statsLoading && (
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} height={78} radius="lg" />
                  ))}
                </SimpleGrid>
              )}
            </Stack>
          </Paper>
          {/* Placeholder cards in the shape of the chart sections, so the
              page doesn't jump when the real charts land. */}
          {statsLoading && (
            <>
              <Skeleton height={300} radius="lg" />
              <Skeleton height={430} radius="lg" />
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Skeleton height={290} radius="lg" />
                <Skeleton height={290} radius="lg" />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Skeleton height={290} radius="lg" />
                <Skeleton height={290} radius="lg" />
              </SimpleGrid>
              <Skeleton height={330} radius="lg" />
            </>
          )}
        </Stack>
      </Container>
    );
  }

  const cohortRows = [...stats.weeklyCohorts].reverse();

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>Activation & Retention</Title>
                <Text size="sm" c="dimmed">
                  How users move from signup to an activated meal plan, and how
                  many keep coming back
                </Text>
              </div>
              {windowControls}
            </Group>

            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <StatTile
                label="Active last 7 days"
                value={stats.totals.activeLast7Days}
                total={stats.totals.users}
              />
              <StatTile
                label="Active last 30 days"
                value={stats.totals.activeLast30Days}
                total={stats.totals.users}
              />
              <StatTile label="Signups last 7 days" value={stats.totals.signupsLast7Days} />
              <StatTile label="Signups last 30 days" value={stats.totals.signupsLast30Days} />
            </SimpleGrid>

            <Group gap="xs">
              <Text size="sm" fw={500}>
                Subscriptions:
              </Text>
              {(
                [
                  ['trial', stats.subscriptions.trial],
                  ['active', stats.subscriptions.active],
                  ['past_due', stats.subscriptions.past_due],
                  ['canceled', stats.subscriptions.canceled],
                ] as const
              ).map(([status, count]) => (
                <Badge key={status} color={SUBSCRIPTION_BADGES[status].color} variant="light">
                  {SUBSCRIPTION_BADGES[status].label}: {count}
                </Badge>
              ))}
              <Badge color="violet" variant="light">
                Lifetime: {stats.subscriptions.lifetime}
              </Badge>
            </Group>
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <ChartHeader
              title="Activation funnel"
              answers="Where do users drop off between signing up and becoming invested? Hover a bar for the step conversion."
            />
            <ActivationFunnel stats={stats} />
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <ChartHeader
              title="Cohort milestones"
              answers="Are newer signup cohorts activating better than older ones? Rows are signup weeks (Mondays), cells are the share of that cohort reaching each milestone."
            />
            <CohortHeatmap cohorts={stats.weeklyCohorts} />
            <details>
              <summary>
                <Text size="xs" c="dimmed" span style={{ cursor: 'pointer' }}>
                  Show as table
                </Text>
              </summary>
              <Table.ScrollContainer minWidth={720}>
                <Table striped withTableBorder mt="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Week of</Table.Th>
                      <Table.Th>Signups</Table.Th>
                      <Table.Th>Onboarded</Table.Th>
                      <Table.Th>Created plan</Table.Th>
                      <Table.Th>Activated plan</Table.Th>
                      <Table.Th>2+ plans</Table.Th>
                      <Table.Th>Swapped</Table.Th>
                      <Table.Th>Active last 7d</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {cohortRows.map((cohort) => (
                      <Table.Tr key={cohort.weekStart}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {cohort.weekStart}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{cohort.signups}</Text>
                        </Table.Td>
                        {[
                          cohort.completedOnboarding,
                          cohort.createdPlan,
                          cohort.activatedPlan,
                          cohort.createdMultiplePlans,
                          cohort.swappedMeal,
                          cohort.activeLast7Days,
                        ].map((count, i) => (
                          <Table.Td key={i}>
                            <Text size="sm">
                              {count}
                              {cohort.signups > 0 && (
                                <Text span size="xs" c="dimmed">
                                  {' '}
                                  ({formatPercent(count, cohort.signups)})
                                </Text>
                              )}
                            </Text>
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </details>
          </Stack>
        </Paper>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Paper withBorder p="md" radius="lg">
            <Stack gap="md">
              <ChartHeader title="Signups per week" answers="Is the top of the funnel growing?" />
              <ColumnChart
                labels={cohortRows.map((c) => c.weekStart.slice(5))}
                values={cohortRows.map((c) => c.signups)}
                unit="signups"
                ariaLabel="Signups per week"
              />
            </Stack>
          </Paper>
          <Paper withBorder p="md" radius="lg">
            <Stack gap="md">
              <ChartHeader
                title="Activation rate by cohort"
                answers="Of each week's signups, how many created and activated a plan?"
              />
              <ActivationRateLines cohorts={stats.weeklyCohorts} />
            </Stack>
          </Paper>
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Paper withBorder p="md" radius="lg">
            <Stack gap="md">
              <ChartHeader
                title="Swappers vs. everyone else"
                answers="Does swapping a meal predict retention? Users who ever swapped, against those who never did."
              />
              <SwapComparisonChart comparison={stats.swapComparison} />
            </Stack>
          </Paper>
          <Paper withBorder p="md" radius="lg">
            <Stack gap="md">
              <ChartHeader
                title="Plans-created distribution"
                answers="How deep does engagement go — one plan and out, or a habit?"
              />
              <ColumnChart
                labels={['0 plans', '1', '2', '3–5', '6+']}
                values={[
                  stats.planCountBuckets['0'],
                  stats.planCountBuckets['1'],
                  stats.planCountBuckets['2'],
                  stats.planCountBuckets['3-5'],
                  stats.planCountBuckets['6+'],
                ]}
                unit="users"
                ariaLabel="Distribution of lifetime plans created"
              />
            </Stack>
          </Paper>
        </SimpleGrid>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <ChartHeader
              title="Weekly retention"
              answers="Of each signup week, what share was active 1, 2, 3… weeks later? Built from per-user weekly activity stamps, which only started collecting when this shipped — early cohorts read low until the stamps accumulate."
            />
            <RetentionTriangle triangle={stats.retentionTriangle} />
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};
